<template>
  <div class="tab-content">
    <div class="app-download-section">
      <div class="hiking-image d-none d-md-block">
        <img :src="hikingImage2" alt="Hiker with GPS device" />
      </div>

      <div class="gps-options">
        <h2 class="display-5 mb-3"><strong>Send to Garmin</strong></h2>

        <div class="device-options">
          <button
            v-if="isUserLoggedIn"
            v-for="device in gpsDevices"
            :key="device.id"
            class="device-option"
            @click="handleGPSDevice(device.id)"
            :disabled="(isSendingToGarmin || isConnectingToGarmin) && device.id === 'garmin'"
          >
            <div class="device-info">
              <img style="max-width: 40px" :src="garminConnect" alt="Connect to Garmin" />
              <div class="device-text">
                <span>{{ device.name }}</span>
                <span v-if="device.id === 'garmin'" class="connection-status">
                  {{ isGarminConnected ? '(Connected)' : '(Not connected)' }}
                </span>
              </div>
            </div>
            <button
              v-if="device.id === 'garmin' && !isGarminConnected && !isSendingToGarmin && !isConnectingToGarmin"
              class="connect-button"
              @click.stop="handleGPSDevice(device.id)"
              :disabled="isConnectingToGarmin"
            >
              Connect
            </button>
            <span
              v-else-if="device.id !== 'garmin' || (!isSendingToGarmin && !isConnectingToGarmin)"
              class="material-icons text-yellow"
            >
              keyboard_arrow_right
            </span>
            <div v-else class="spinner-small"></div>
          </button>

          <button
            v-else
            v-for="device in gpsDevices"
            :key="device.id"
            class="device-option"
            @click="handleLogin"
          >
            <div class="device-info">
              <img style="max-width: 40px" :src="garminConnect" alt="Connect to Garmin" />
              <div class="device-text">
                <span>{{ device.name }}</span>
                <span v-if="device.id === 'garmin'" class="connection-status">
                  {{ isGarminConnected ? '(Connected)' : '(Not connected)' }}
                </span>
              </div>
            </div>
            <span class="d-flex align-items-center">
              <span class="mr-1" style="width: 95px; line-height: 1;"><small class="text-muted" style="font-size: .75em">Sign in to sync</small></span>
              <span class="material-icons text-muted">keyboard_arrow_right</span>
            </span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onUnmounted } from 'vue';
import graphClient from '../../utils/graphql/graph_client';
import hikingImage2 from '../../images/download-modal-3.webp';
import garminConnect from '../../images/garmin-connect.webp';
import { OnBoardingGoal } from "../../utils/models/OnBoardingGoal";
import { SEND_TO_GARMIN } from '../../utils/graphql/send-to-garmin';
import { createGarminConnector } from '../../utils/helpers/connectGarminHelper';
import { getDownloadResourceContext, getResourceNoun } from './context';

// Props
const props = defineProps<{
  userData: any;
}>();

// Emits
const emit = defineEmits<{
  close: [];
}>();

const isUserLoggedIn = computed(() => props.userData != null);

const resourceContext = computed(() => getDownloadResourceContext());
const resourceNoun = computed(() => getResourceNoun(resourceContext.value.graphqlType));
const resourceLabelTitleCase = computed(() =>
  resourceNoun.value.charAt(0).toUpperCase() + resourceNoun.value.slice(1)
);

const handleLogin = () => {
  if (window.onBoardingVue && window.onBoardingVue.launchFor) {
    window.onBoardingVue.launchFor(OnBoardingGoal.ConnectToApps);
  }
};

// Data
const isSendingToGarmin = ref(false);
const isConnectingToGarmin = ref(false);
const garminConnector = createGarminConnector();

const gpsDevices = [
  { id: 'garmin', name: 'Garmin', icon: '⚡', color: '#00a8e8' }
];

// Computed
const isGarminConnected = computed(() => props.userData?.garmin_connected || false);

// Methods
const updateGarminConnectionStatus = () => {
  if (props.userData) {
    props.userData.garmin_connected = true;
  }

  if (window.user) {
    window.user.garmin_connected = true;
  }
};

const sendResourceToGarmin = async () => {
  try {
    isSendingToGarmin.value = true;

    const context = resourceContext.value;
    const resourceId = context.id;

    if (!resourceId) {
      alert(`${resourceLabelTitleCase.value} ID not found. Please try again from a ${resourceNoun.value} page.`);
      return;
    }

    const response = await graphClient.request(SEND_TO_GARMIN, {
      klass: context.graphqlType,
      id: resourceId
    });

    if (response.sendToGarmin) {
      alert(`${resourceLabelTitleCase.value} successfully sent to your Garmin device! It will appear in Garmin Connect shortly.`);
      emit('close');
    } else {
      alert(`Failed to send ${resourceNoun.value} to Garmin. Please try again.`);
    }
  } catch (error: any) {
    console.error('Error sending to Garmin:', error);
    alert(error.response?.errors?.[0]?.message || `Failed to send ${resourceNoun.value} to Garmin. Please try again.`);
  } finally {
    isSendingToGarmin.value = false;
  }
};

const handleGPSDevice = async (device: string) => {
  if (device !== 'garmin') {
    console.log(`Send to ${device} clicked`);
    return;
  }

  if (!isGarminConnected.value) {
    try {
      isConnectingToGarmin.value = true;
      await garminConnector.connect();
      updateGarminConnectionStatus();
      await sendResourceToGarmin();
    } catch (error: any) {
      const message = error?.message || 'Failed to connect to Garmin. Please try again.';
      alert(message);
    } finally {
      isConnectingToGarmin.value = false;
    }

    return;
  }

  await sendResourceToGarmin();
};

// Clean up on unmount
onUnmounted(() => {
  garminConnector.cancel();
});
</script>

<style scoped lang="scss">
.tab-content {
  animation: fadeIn 0.3s ease-in-out;
  padding: 2em;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.app-download-section {
  display: flex;
  gap: 32px;
  align-items: flex-start;
}

.hiking-image {
  flex: 0 0 300px;

  img {
    width: 100%;
    height: auto;
    border-radius: 8px;
  }
}

.gps-options {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;

  h2 {
    font-size: 24px;
    margin-bottom: 32px;
    font-weight: 600;
  }
}

.device-options {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.device-option {
  background-color: transparent;
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  padding: 16px 24px;
  border-radius: 8px;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  justify-content: space-between;
  align-items: center;

  &:hover {
    background-color: rgba(255, 255, 255, 0.05);
    border-color: rgba(255, 255, 255, 0.3);
  }

  .device-info {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .device-text {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }

  .connection-status {
    font-size: 10px;
    color: rgba(255, 255, 255, 0.6);
  }

  .arrow-icon {
    color: rgba(255, 255, 255, 0.5);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.spinner-small {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-top-color: #00a8e8;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.connect-button {
  background-color: #00a8e8;
  color: white;
  border: none;
  padding: 6px 16px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #0090cc;
  }

  &:active {
    background-color: #0080bb;
  }
}

@media (max-width: 768px) {
  .app-download-section {
    flex-direction: column;
  }

  .hiking-image {
    flex: none;
    height: 200px;
  }
}
</style>
